import { useState, useRef, useEffect } from 'react';
import { CardViewerRenderedFields, renderFields } from '@highnoteplatform/card-viewer';

interface UseHighnoteCardViewerOptions {
  clientToken?: string;
  paymentCardId?: string;
  isUsersCard?: boolean;
  showFullCardNumber?: boolean;
  styles?: {
    fontSize?: string;
    color?: string;
    fontWeight?: string;
    letterSpacing?: string;
    fontFamily?: string;
  };
  enableClipboard?: boolean;
  onTokenExpired?: () => void;
}

interface UseHighnoteCardViewerReturn {
  sdk?: CardViewerRenderedFields;
  showCardInfo: boolean;
  cardNumberVisible: boolean;
  toggleCardNumberVisibility: () => void;
}

export const useHighnoteCardViewer = ({
  clientToken,
  paymentCardId,
  isUsersCard = false,
  showFullCardNumber = false,
  styles = {},
  enableClipboard = false,
  onTokenExpired,
}: UseHighnoteCardViewerOptions): UseHighnoteCardViewerReturn => {
  const [sdk, setSdk] = useState<CardViewerRenderedFields>();
  const sdkRef = useRef<CardViewerRenderedFields>();
  const [showCardInfo, setShowCardInfo] = useState(false);
  const [cardNumberVisible, setCardNumberVisible] = useState(false);
  const hasMountedSdk = useRef(false);
  const isMountedRef = useRef(true);

  const clearCardData = async (cardId: string): Promise<void> => {
    return new Promise((resolve) => {
      const cardNumberEl = document.getElementById(`cardNumber-${cardId}`);
      const expirationDateEl = document.getElementById(`expirationDate-${cardId}`);
      const cvvEl = document.getElementById(`cvv-${cardId}`);

      if (cardNumberEl) {
        cardNumberEl.innerHTML = '';
        cardNumberEl.removeAttribute('data-highnote-field');
      }
      if (expirationDateEl) {
        expirationDateEl.innerHTML = '';
        expirationDateEl.removeAttribute('data-highnote-field');
      }
      if (cvvEl) {
        cvvEl.innerHTML = '';
        cvvEl.removeAttribute('data-highnote-field');
      }
      resolve();
    });
  };

  const mountSDK = async () => {
    if (!clientToken || !paymentCardId) return;

    return await renderFields({
      clientToken: clientToken,
      paymentCardId: paymentCardId,
      enableClipboard,
      environment: process.env.REACT_APP_ENV === 'production' ? 'live' : 'test',
      onError: (error: unknown) => {
        // eslint-disable-next-line no-console
        console.warn('SDK error for card:', paymentCardId, error);

        // Check if error indicates token expiration/authentication issues
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (
          errorMessage.toLowerCase().includes('token') ||
          errorMessage.toLowerCase().includes('auth') ||
          errorMessage.toLowerCase().includes('expired')
        ) {
          onTokenExpired?.();
        }
      },
      elements: {
        cardNumber: {
          selector: `#cardNumber-${paymentCardId}`,
          styles: {
            fontSize: '20px',
            color: '#ffffff',
            fontWeight: '600',
            letterSpacing: '-1px',
            fontFamily: 'Roboto, sans-serif',
            ...styles,
          },
        },
        expirationDate: {
          selector: `#expirationDate-${paymentCardId}`,
          styles: {
            fontSize: '16px',
            color: styles.color || '#ffffff',
            fontWeight: '500',
            fontFamily: 'Roboto, sans-serif',
          },
        },
        cvv: {
          selector: `#cvv-${paymentCardId}`,
          styles: {
            fontSize: '16px',
            color: styles.color || '#ffffff',
            fontWeight: '500',
            fontFamily: 'Roboto, sans-serif',
          },
        },
      },
    });
  };

  const toggleCardNumberVisibility = () => {
    sdkRef.current?.toggleCardNumberMask();
    setCardNumberVisible((prev) => !prev);
  };

  // Effect to control when to show card info
  useEffect(() => {
    if (isUsersCard && clientToken && showFullCardNumber) {
      setShowCardInfo(true);
    } else {
      setShowCardInfo(false);
      // Clean up SDK when conditions change
      if (sdkRef.current) {
        sdkRef.current.unmount?.();
        setSdk(undefined);
        sdkRef.current = undefined;
      }
      if (paymentCardId) {
        clearCardData(paymentCardId);
      }
    }
  }, [isUsersCard, clientToken, showFullCardNumber, paymentCardId]);

  // Effect to mount SDK after DOM elements are rendered
  useEffect(() => {
    if (isUsersCard && clientToken && showCardInfo && !hasMountedSdk.current && paymentCardId) {
      hasMountedSdk.current = true;

      const rafId = requestAnimationFrame(() => {
        if (!isMountedRef.current) return;

        clearCardData(paymentCardId).then(() => {
          if (!isMountedRef.current) return;

          mountSDK()
            .then((response) => {
              if (response && isMountedRef.current) {
                setSdk(response);
                sdkRef.current = response;
              } else {
                hasMountedSdk.current = false;
              }
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.error('❌ SDK mount failed for card:', paymentCardId, error);
              hasMountedSdk.current = false;
            });
        });
      });

      return () => {
        cancelAnimationFrame(rafId);
        if (sdkRef.current) {
          sdkRef.current.unmount?.();
        }
        hasMountedSdk.current = false;
        if (paymentCardId) {
          clearCardData(paymentCardId);
        }
      };
    }

    return () => {
      if (sdkRef.current) {
        sdkRef.current.unmount?.();
      }
      hasMountedSdk.current = false;
      if (paymentCardId) {
        clearCardData(paymentCardId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUsersCard, clientToken, showCardInfo, paymentCardId]);

  // Effect to track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    sdk,
    showCardInfo,
    cardNumberVisible,
    toggleCardNumberVisibility,
  };
};
